/**
 * Per-run eval variables (gh #3007).
 *
 * A scenario may declare `variables: Record<string, unknown[]>` — a map of
 * author-provided arrays. When the scenario runs `runs: N` times, run *i*
 * (1-based) resolves `{{var(NAME)}}` against `variables.NAME[i-1]`, so a
 * data-mutating eval can lease a disjoint fixture per run instead of colliding
 * on one shared seed row. The value at an index may be an object, so one index
 * yields a coherent bundle (`{{var(case).patient_id}}`, `{{var(case).slot_id}}`
 * move together). Resolution is done ONCE per run over the whole scenario
 * snapshot, so the prompt (`input`/`history`) and the assertion (`expected`/
 * `evaluator_instructions`) always see the same value.
 *
 * Seeding/reset of the underlying fixtures stays agent-managed — this module is
 * only the templating + per-run indexing primitive.
 */

import { resolvePlaceholdersDeep, type PlaceholderContext } from './scheduling/placeholders';

/** Snapshot resolution touches ONLY `{{var()}}` — other placeholder functions
 * (`{{now()}}`, `{{state()}}`, …) stay literal in the frozen snapshot, matching
 * pre-variables behavior. `input` still resolves them later at fire time. */
const VAR_ONLY_FUNCTIONS: ReadonlySet<string> = new Set(['var']);

/** Scenario-level `variables:` — a map of author-provided arrays. */
export type EvalScenarioVariables = Record<string, unknown[]>;

/** A variable array that is too short to cover the scenario's run count. */
export interface InsufficientEvalVariable {
  variable: string;
  array_length: number;
  required_runs: number;
}

/** True when `value` is a usable scenario-variables map (non-empty, all arrays). */
export function hasEvalVariables(value: unknown): value is EvalScenarioVariables {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const entries = Object.entries(value as Record<string, unknown>);
  return entries.length > 0 && entries.every(([, v]) => Array.isArray(v));
}

/**
 * Validate that every declared variable array covers `numberOfRuns` (run i reads
 * index i-1, so an array must have at least `numberOfRuns` entries). Returns the
 * offending arrays; empty when all cover the run count. Callers reject rather
 * than wrap/modulo — a short array is an authoring error, and silently reusing a
 * fixture across runs would re-introduce the self-collision this feature removes.
 */
export function findInsufficientEvalVariables(
  variables: EvalScenarioVariables,
  numberOfRuns: number,
): InsufficientEvalVariable[] {
  const out: InsufficientEvalVariable[] = [];
  for (const [name, arr] of Object.entries(variables)) {
    if (arr.length < numberOfRuns) {
      out.push({ variable: name, array_length: arr.length, required_runs: numberOfRuns });
    }
  }
  return out;
}

/**
 * Build the index-free row for run at 0-based `index`:
 * `{ NAME: variables.NAME[index] }` for every declared variable. Out-of-range
 * indices yield `undefined` (→ `{{var(NAME)}}` resolves empty); callers should
 * validate with `findInsufficientEvalVariables` first.
 */
export function evalVariablesRow(
  variables: EvalScenarioVariables,
  index: number,
): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  for (const [name, arr] of Object.entries(variables)) {
    row[name] = arr[index];
  }
  return row;
}

/**
 * Resolve `{{var(...)}}` (and `{{var(...).path}}`) in every string leaf of a
 * scenario snapshot for run at 0-based `index`. Non-string leaves and
 * non-`var` placeholders pass through untouched.
 */
export function resolveEvalScenarioSnapshot<T>(
  snapshot: T,
  variables: EvalScenarioVariables,
  index: number,
): T {
  const context: PlaceholderContext = { variables: evalVariablesRow(variables, index) };
  return resolvePlaceholdersDeep(snapshot, context, { only: VAR_ONLY_FUNCTIONS });
}
